/**
 * First-party site analytics (see supabase/functions/site-track).
 *
 * - Visitor ID: random UUID in localStorage + a 1-year first-party cookie, so
 *   either surviving restores the other. Browsers sending Global Privacy
 *   Control get a session-only ID instead (sessionStorage, no cookie) and the
 *   server stores no IP for them.
 * - Session: shared across tabs; ends after 30 min of inactivity, and a fresh
 *   one starts when someone arrives again from an external link or campaign.
 * - Events are queued and sent in small batches as text/plain (no CORS
 *   preflight), with a final sendBeacon when the tab is hidden.
 * - Forms call getAnalyticsIds() so each lead is linked to its journey; the
 *   server records the submission itself, so forms don't send their own event.
 * - Nothing is collected without analytics consent (src/lib/consent.ts):
 *   opt-in in the EEA / UK / CH / NO, opt-out elsewhere. Withdrawing consent
 *   deletes the visitor and session IDs from the browser.
 * - /admin pages are never tracked. Nothing here may ever throw into the page.
 */

import { ANALYTICS_ENABLED } from "@/config/features";
import { getConsent, onConsentChange } from "@/lib/consent";

const VID_KEY = "enzo_vid";
const SESSION_KEY = "enzo_sess";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const FLUSH_DELAY_MS = 4000;
const FLUSH_AT = 10;
const SITE_HOSTS = ["enzoscleaning.com", "www.enzoscleaning.com"];
const UTM_KEYS = ["source", "medium", "campaign", "term", "content"] as const;

type Utm = Partial<Record<(typeof UTM_KEYS)[number], string>>;
interface SessionState { id: string; last: number; started_at: string; landing_path: string; referrer: string; utm: Utm }
interface QueuedEvent { type: string; path: string; at: string; data: Record<string, string | number | boolean | null> }

const endpoint = () => `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/site-track`;
const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";
const gpc = () => isBrowser() && (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
const tracked = (path: string) => ANALYTICS_ENABLED && !path.startsWith("/admin") && getConsent().analytics;
const uuid = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
        (Number(c) ^ (Math.random() * 16) >> (Number(c) / 4)).toString(16));

function read(store: Storage | undefined, key: string): string | null {
  try { return store?.getItem(key) ?? null; } catch { return null; }
}
function write(store: Storage | undefined, key: string, value: string) {
  try { store?.setItem(key, value); } catch { /* storage blocked */ }
}
function readCookie(name: string): string | null {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}
function writeCookie(name: string, value: string) {
  try {
    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=31536000; Path=/; SameSite=Lax${secure}`;
  } catch { /* cookies blocked */ }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function visitorId(): string {
  if (gpc()) {
    let id = read(sessionStorage, VID_KEY);
    if (!id || !UUID_RE.test(id)) { id = uuid(); write(sessionStorage, VID_KEY, id); }
    return id;
  }
  let id = read(localStorage, VID_KEY) || readCookie(VID_KEY);
  if (!id || !UUID_RE.test(id)) id = uuid();
  write(localStorage, VID_KEY, id);
  writeCookie(VID_KEY, id);
  return id;
}

function sessionStore(): Storage | undefined {
  try { return gpc() ? sessionStorage : localStorage; } catch { return undefined; }
}

function externalArrival(): { referrer: string; utm: Utm; isExternal: boolean } {
  const referrer = document.referrer || "";
  let refHost = "";
  try { refHost = referrer ? new URL(referrer).hostname.toLowerCase() : ""; } catch { /* bad referrer */ }
  const params = new URLSearchParams(location.search);
  const utm: Utm = {};
  for (const k of UTM_KEYS) {
    const v = params.get(`utm_${k}`);
    if (v) utm[k] = v.slice(0, 200);
  }
  const fromOutside = !!refHost && !SITE_HOSTS.includes(refHost) && !refHost.endsWith(".enzoscleaning.com");
  return { referrer, utm, isExternal: fromOutside || Object.keys(utm).length > 0 };
}

let session: SessionState | null = null;
let firstPageOfDocument = true;

function currentSession(path: string): SessionState {
  const now = Date.now();
  if (!session) {
    try { session = JSON.parse(read(sessionStore(), SESSION_KEY) || "null"); } catch { session = null; }
  }
  // A new session on inactivity, or when this document load arrived from
  // outside — unless it's the same arrival again (a reload keeps the original
  // document.referrer, and that shouldn't split the visit).
  const arrival = firstPageOfDocument ? externalArrival() : null;
  firstPageOfDocument = false;
  const expired = !session || now - session.last > SESSION_TIMEOUT_MS || !UUID_RE.test(session.id);
  const sameArrival = !!session && !!arrival &&
    session.referrer === arrival.referrer && JSON.stringify(session.utm) === JSON.stringify(arrival.utm);
  if (expired || (arrival?.isExternal && !sameArrival)) {
    session = {
      id: uuid(), last: now, started_at: new Date(now).toISOString(), landing_path: path,
      // Only a full page load has a meaningful document.referrer; a session that
      // restarts mid-visit (timeout during SPA navigation) is marked internal.
      referrer: arrival ? arrival.referrer : location.origin + "/",
      utm: arrival?.utm || {},
    };
  }
  session.last = now;
  write(sessionStore(), SESSION_KEY, JSON.stringify(session));
  return session;
}

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function flush(useBeacon = false) {
  if (timer) { clearTimeout(timer); timer = null; }
  if (!queue.length || !session) return;
  const events = queue;
  queue = [];
  const body = JSON.stringify({
    v: visitorId(),
    s: session.id,
    gpc: gpc(),
    wd: (navigator as Navigator & { webdriver?: boolean }).webdriver === true,
    session: { landing_path: session.landing_path, referrer: session.referrer, started_at: session.started_at, utm: session.utm },
    events,
  });
  try {
    const blob = new Blob([body], { type: "text/plain;charset=UTF-8" });
    if (useBeacon && navigator.sendBeacon?.(endpoint(), blob)) return;
    void fetch(endpoint(), { method: "POST", body, keepalive: true, headers: { "Content-Type": "text/plain;charset=UTF-8" } })
      .catch(() => { /* analytics is best-effort */ });
  } catch { /* best-effort */ }
}

/** Queue an analytics event. Never throws. */
export function trackEvent(type: string, data: QueuedEvent["data"] = {}, path?: string) {
  try {
    if (!isBrowser()) return;
    const p = path ?? location.pathname;
    if (!tracked(p)) return;
    currentSession(p);
    queue.push({ type, path: p, at: new Date().toISOString(), data });
    if (queue.length >= FLUSH_AT) flush();
    else if (!timer) timer = setTimeout(() => flush(), FLUSH_DELAY_MS);
  } catch { /* best-effort */ }
}

/** Call on every route change (see useAnalytics). */
export function trackPageView(path: string) {
  try {
    if (!isBrowser() || !tracked(path)) return;
    trackEvent("page_view", { title: document.title.slice(0, 200), query: location.search.slice(0, 300) || null }, path);
  } catch { /* best-effort */ }
}

/**
 * What to send with a form / order request so the lead links to its journey:
 * the IDs when tracking, `{ no_consent: true }` when the visitor declined
 * analytics (the server then never tries to match them by IP either), or
 * null when analytics is switched off site-wide.
 */
export function getAnalyticsIds(): { visitor_id: string; session_id: string } | { no_consent: true } | null {
  try {
    if (!isBrowser() || !ANALYTICS_ENABLED) return null;
    if (!getConsent().analytics) return { no_consent: true };
    if (!tracked(location.pathname)) return null;
    const s = currentSession(location.pathname);
    const ids = { visitor_id: visitorId(), session_id: s.id };
    flush(); // make sure the steps leading up to the submission are on the server
    return ids;
  } catch { return null; }
}

const linkText = (el: Element) => (el.textContent || el.getAttribute("aria-label") || "").replace(/\s+/g, " ").trim().slice(0, 80);
const formsStarted = new WeakSet<HTMLFormElement>();
let listening = false;

/** Removes our IDs from this browser (consent withdrawn). */
function forgetVisitor() {
  queue = [];
  session = null;
  for (const store of [() => localStorage, () => sessionStorage]) {
    try { store().removeItem(VID_KEY); store().removeItem(SESSION_KEY); } catch { /* storage blocked */ }
  }
  try { document.cookie = `${VID_KEY}=; Max-Age=0; Path=/; SameSite=Lax`; } catch { /* cookies blocked */ }
}

/** Attaches the document-level listeners once. */
export function initAnalyticsListeners() {
  if (!ANALYTICS_ENABLED || !isBrowser() || listening) return;
  listening = true;

  // Consent given mid-visit: start with the page they're on. Withdrawn: forget them.
  onConsentChange((c) => {
    if (c.analytics) { firstPageOfDocument = true; trackPageView(location.pathname); }
    else forgetVisitor();
  });

  document.addEventListener("click", (e) => {
    try {
      const target = e.target as Element | null;
      const a = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (a) {
        const href = a.getAttribute("href") || "";
        if (href.startsWith("tel:")) return trackEvent("phone_tap", { number: href.slice(4), text: linkText(a) });
        if (href.startsWith("mailto:")) return trackEvent("email_tap", { text: linkText(a) });
        let url: URL | null = null;
        try { url = new URL(a.href, location.href); } catch { /* ignore */ }
        if ((url && /\.(pdf|zip|docx?|xlsx?)$/i.test(url.pathname)) || a.hasAttribute("download")) {
          return trackEvent("download", { href: url ? url.pathname : href, text: linkText(a) });
        }
        if (url && url.host !== location.host) return trackEvent("outbound_link", { href: url.href.slice(0, 300), text: linkText(a) });
        return trackEvent("click", { href: url ? url.pathname : href, text: linkText(a) });
      }
      const btn = target?.closest?.("button, [role='button']");
      if (btn && !btn.closest("form")) trackEvent("click", { element: "button", text: linkText(btn) });
    } catch { /* best-effort */ }
  }, { capture: true });

  // First interaction with a form (never the values typed).
  document.addEventListener("focusin", (e) => {
    try {
      const field = e.target as Element | null;
      const form = field?.closest?.("form") as HTMLFormElement | null;
      if (!form || formsStarted.has(form) || !field?.matches("input, textarea, select")) return;
      formsStarted.add(form);
      trackEvent("form_start", {
        form: (form.getAttribute("name") || form.id || form.getAttribute("aria-label") || "form").slice(0, 80),
      });
    } catch { /* best-effort */ }
  });

  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flush(true); });
  window.addEventListener("pagehide", () => flush(true));
}
